// src/tabs/TabActivation.js
// ✅ keep-alive 탭 공통 인프라 — 탭이 숨겨졌다가(display:none) 다시 보일 때 "지금
// 활성화됐다"는 신호가 필요한 페이지가 opt-in으로 구독하는 훅. AppLayout.jsx가
// keep-alive 뱅크의 각 페이지 인스턴스를 TabPathContext.Provider로 감싸서 "이
// 인스턴스가 대표하는 경로"를 알려주고(그 인스턴스는 실제 라우트 매칭 밖에서
// 렌더되므로 useLocation()만으로는 자기 경로를 알 수 없음), useTabActivated 훅이
// 그 경로와 현재 활성 경로(useLocation())를 비교해 전환을 감지한다.
import { createContext, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// 값 = 이 서브트리가 대표하는 경로. keep-alive 뱅크 밖(=일반 <Outlet/> 렌더)에서는
// null이며, 그 경우 훅은 useLocation()의 실제 현재 경로를 그대로 쓴다.
export const TabPathContext = createContext(null);

/**
 * 이 페이지의 탭이 "숨겨져 있다가 다시 활성화될 때"(재방문) callback을 실행한다.
 * 최초 마운트 시점(=처음 여는 순간)에는 페이지 자신의 마운트 useEffect가 이미
 * 데이터를 불러오므로 중복 호출하지 않는다 — "재방문"만 감지한다.
 *
 * 사용 예:
 *   useTabActivated(() => loadList(search));
 */
export function useTabActivated(callback) {
  const myPath = useContext(TabPathContext);
  const loc = useLocation();
  const representedPath = myPath ?? loc.pathname;
  const isActiveNow = representedPath === loc.pathname;

  // 최초 렌더에서는 "이미 활성 상태였다"로 간주 — 마운트 직후 중복 호출 방지.
  const wasActiveRef = useRef(true);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (isActiveNow && !wasActiveRef.current) {
      callbackRef.current?.();
    }
    wasActiveRef.current = isActiveNow;
  }, [isActiveNow]);
}
